import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { DataTable, useDataTable, type DataTableFilter, type DataTableRefs } from '@odo/shared-ui';
import {
    useCharges,
    useInfiniteCharges,
    usePracticeOptions,
    useRetryCharge,
    useCancelCharge
} from '../../hooks/useBilling';
import { DATA_TABLE_ROW_HEIGHT } from '../../hooks/useViewportPageSize';
import { useViewportTableLimit } from '../../hooks/useViewportTableLimit';
import { CreateChargeModal } from '../../components/billing/CreateChargeModal';
import { chargeResultToast } from '../../components/billing/chargeResultToast';
import { ChargeDrawer, CancelChargeModal } from '../../components/billing/ChargeDrawer';
import { ChargeStatusCards } from '../../components/billing/ChargeStatusCards';
import {
    buildChargeColumns,
    type ChargesSortBy
} from '../../components/billing/charges/chargeColumns';
import { usePageHeader } from '../../providers/PageHeaderProvider';
import { usePageTitle } from '../../hooks/usePageTitle';
import { useToast } from '../../providers/ToastProvider';
import { useMyPermissions } from '../../hooks/useMyPermissions';
import { apiErrorMessage } from '../../utils/apiError';
import { BILLING_LIST_SEARCH_MAX_LENGTH } from '../../services/billing';

const SEARCH_DEBOUNCE_MS = 300;
const STATUS_OPTIONS = ['pending', 'processing', 'invoiced', 'error', 'canceled'] as const;
// Starting limit only: the paged fetch waits for the viewport measurement.
const VIEWPORT_DEFAULT_PAGE_SIZE = 20;

const STATUS_FILTER_STORAGE_KEY = 'fv_billing_statusFilter';
const PRACTICE_FILTER_STORAGE_KEY = 'fv_billing_practiceFilter';

type ChargeFilters = { status: string[]; practiceId: string[] };

// Biggest/newest first, everything else A-Z first - the order applied when
// sorting moves to a different column (matches the pre-migration handleSort).
const defaultOrderFor = (by: ChargesSortBy) =>
    by === 'date' || by === 'total' ? ('desc' as const) : ('asc' as const);

export function ChargesList() {
    usePageTitle('Charges');
    const navigate = useNavigate();
    const { chargeId } = useParams<{ chargeId: string }>();
    const { setTitle, setSubtitle } = usePageHeader();
    const { showToast } = useToast();
    const { hasPermission } = useMyPermissions();
    const canEditBilling = hasPermission('billing.update');

    // Page, limit, sort, search and both filters - plus the page-reset rules
    // and sessionStorage persistence this page used to hand-roll - all live
    // in the hook now. Same two keys/format as before, so a user's stored
    // filters survive this migration.
    const table = useDataTable<ChargesSortBy, ChargeFilters>({
        defaultSort: { by: 'date', order: 'desc' },
        defaultLimit: VIEWPORT_DEFAULT_PAGE_SIZE,
        defaultFilters: { status: [], practiceId: [] },
        defaultOrderFor,
        filterStorageKeys: {
            status: STATUS_FILTER_STORAGE_KEY,
            practiceId: PRACTICE_FILTER_STORAGE_KEY
        }
    });
    const { status: statusFilter, practiceId: practiceFilter } = table.filters;
    const isInfinite = table.mode === 'infinite';

    const [showCreateModal, setShowCreateModal] = useState(false);
    const [cancelTargetId, setCancelTargetId] = useState<string | null>(null);

    const { mutateAsync: retryCharge, isPending: isRetrying } = useRetryCharge();
    const { mutateAsync: cancelCharge, isPending: isCanceling } = useCancelCharge();

    const [searchInput, setSearchInput] = useState('');
    const { setSearch: commitSearch } = table;
    useEffect(() => {
        const handle = setTimeout(() => commitSearch(searchInput.trim()), SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(handle);
    }, [searchInput, commitSearch]);

    const {
        isReady: pageSizeReady,
        tableCardRef,
        tableHeaderRef,
        tableBodyRef,
        paginationRef
    } = useViewportTableLimit(table, { rowHeight: DATA_TABLE_ROW_HEIGHT });

    const { page, limit, sort, order } = table.params;
    const sharedChargeParams = {
        limit,
        q: table.search || undefined,
        status: statusFilter[0],
        practiceId: practiceFilter[0],
        sort,
        order
    };
    // Both hooks are called unconditionally (React's rules of hooks) and only
    // one is ever `enabled` - same split as InvoicesContent's paged/infinite pair.
    const paged = useCharges({
        ...sharedChargeParams,
        page,
        enabled: !isInfinite && pageSizeReady
    });
    const infinite = useInfiniteCharges({ ...sharedChargeParams, enabled: isInfinite });

    const charges = isInfinite ? infinite.charges : paged.charges;
    const meta = isInfinite ? infinite.meta : paged.meta;
    const isLoading = isInfinite ? infinite.isLoading : paged.isLoading;
    const isFetching = isInfinite ? infinite.isFetching : paged.isFetching;

    const { practices } = usePracticeOptions();
    const totalPages = Math.max(1, Math.ceil(meta.total / limit));

    const statusCounts = meta.statusCounts ?? {};
    const allCount = Object.values(statusCounts).reduce((sum, n) => sum + n, 0);

    useEffect(() => {
        setTitle('Charges');
        setSubtitle(`${allCount} total`);
        return () => {
            setTitle(null);
            setSubtitle(null);
        };
    }, [allCount, setTitle, setSubtitle]);

    const practiceOptions = useMemo(
        () =>
            practices.map((p) =>
                p.doctorName
                    ? {
                          value: p.id,
                          label: p.name,
                          sublabel: `Dr. ${p.doctorName}`,
                          keywords: [`Dr. ${p.doctorName}`, p.doctorName]
                      }
                    : { value: p.id, label: p.name }
            ),
        [practices]
    );

    const openCharge = useCallback((id: string) => navigate(`/billing/charges/${id}`), [navigate]);
    const closeDrawer = () => navigate('/billing/charges');

    const handleRetry = useCallback(
        async (id: string) => {
            try {
                const { message, type } = chargeResultToast(await retryCharge({ id }), 'retry');
                showToast(message, type);
            } catch (error) {
                showToast(apiErrorMessage(error, 'Failed to retry charge'), 'error');
            }
        },
        [retryCharge, showToast]
    );

    const handleCancel = async (reason: string) => {
        if (!cancelTargetId) return;
        try {
            await cancelCharge({ id: cancelTargetId, reason });
            showToast('Charge canceled', 'success');
            setCancelTargetId(null);
        } catch (error) {
            showToast(apiErrorMessage(error, 'Failed to cancel charge'), 'error');
        }
    };

    const columns = useMemo(
        () =>
            buildChargeColumns({
                canEdit: canEditBilling,
                isRetrying,
                onOpen: openCharge,
                onRetry: (id) => {
                    void handleRetry(id);
                },
                onCancel: setCancelTargetId
            }),
        [openCharge, handleRetry, canEditBilling, isRetrying]
    );

    // Status keeps its multi-select UI (only the first value is ever sent to
    // the endpoint) and `searchable: true` on both pills - the old
    // FilterDropdown always showed its in-menu search box regardless of
    // option count, unlike the shared dropdown's >8-option default.
    const filters: DataTableFilter[] = [
        {
            key: 'status',
            label: 'Status',
            multiple: true,
            options: STATUS_OPTIONS as unknown as string[],
            value: statusFilter,
            onChange: (value) => table.setFilter('status', value),
            workQueueVariant: true,
            align: 'right',
            searchable: true
        },
        {
            key: 'practiceId',
            label: 'Practice',
            multiple: true,
            options: practiceOptions,
            value: practiceFilter,
            onChange: (value) => table.setFilter('practiceId', value),
            workQueueVariant: true,
            align: 'right',
            searchable: true
        }
    ];

    const refs: DataTableRefs = {
        card: tableCardRef,
        head: tableHeaderRef,
        body: tableBodyRef,
        pagination: paginationRef
    };

    return (
        <div className="flex flex-col h-full overflow-hidden bg-gray-100">
            {/* Status filter cards */}
            <div className="shrink-0">
                <ChargeStatusCards
                    statusCounts={statusCounts}
                    selected={statusFilter}
                    onChange={(value) => table.setFilter('status', value)}
                />
            </div>

            <DataTable
                label="Charges"
                columns={columns}
                rows={charges}
                getRowKey={(charge) => charge.id}
                rowAction={{ onActivate: (charge) => openCharge(charge.id), mode: 'click' }}
                rowClassName={(charge) => (charge.status === 'error' ? 'bg-red-50/40' : undefined)}
                sort={{ by: table.sort, order: table.order, onSort: table.toggleSort }}
                mode={table.mode}
                onModeChange={table.setMode}
                pagination={{ page, totalPages, total: meta.total, onPage: table.setPage }}
                onLoadMore={infinite.fetchNextPage}
                hasMore={infinite.hasNextPage}
                isLoadingMore={infinite.isFetchingNextPage}
                isLoading={isLoading}
                loadingRows={
                    <tr>
                        <td colSpan={9} className="px-3 py-8 text-center">
                            <div className="flex items-center justify-center gap-2 text-gray-400">
                                <div className="w-4 h-4 rounded-full border-2 border-gray-300 border-t-gray-600 animate-spin" />
                                <span className="text-sm">Loading charges…</span>
                            </div>
                        </td>
                    </tr>
                }
                emptyState="No charges found."
                refs={refs}
                className="overflow-hidden px-8 pb-8"
                search={{
                    value: searchInput,
                    onChange: setSearchInput,
                    placeholder: 'Search practice, patient, or product…',
                    maxLength: BILLING_LIST_SEARCH_MAX_LENGTH
                }}
                filters={filters}
                toolbar={
                    isFetching && !isLoading ? (
                        <span className="text-xs text-gray-400">Refreshing…</span>
                    ) : undefined
                }
                actions={
                    canEditBilling
                        ? [
                              {
                                  key: 'new-charge',
                                  label: '+ New charge',
                                  variant: 'primary',
                                  onClick: () => setShowCreateModal(true)
                              }
                          ]
                        : undefined
                }
            />

            {chargeId && <ChargeDrawer chargeId={chargeId} onClose={closeDrawer} />}

            {showCreateModal && (
                <CreateChargeModal
                    onClose={() => setShowCreateModal(false)}
                    onSuccess={(created) => {
                        setShowCreateModal(false);
                        void paged.refresh();
                        openCharge(created.id);
                    }}
                />
            )}

            {cancelTargetId && (
                <CancelChargeModal
                    onConfirm={(reason) => {
                        void handleCancel(reason);
                    }}
                    onClose={() => setCancelTargetId(null)}
                    isPending={isCanceling}
                />
            )}
        </div>
    );
}
